import React, { useState, useEffect } from 'react';
import { RotateCcw } from 'lucide-react';

const ITEMS = ['🍖', '🦴', '🥎', '🦮', '🛏️', '🏠', '🏥', '🚑', '🏨', '🏆', '👑', '🌟', '🌍', '🚀'];
const ITEM_NAMES = ['Premio', 'Hueso', 'Pelota', 'Correa', 'Cama', 'Casita', 'Veterinaria', 'Ambulancia', 'Hospital', 'Trofeo', 'Rey Peludo', 'Estrella', 'Mundo', 'Galaxy'];

const GRID_ROWS = 5;
const GRID_COLS = 5;
const TOTAL_SLOTS = GRID_ROWS * GRID_COLS;

interface Slot {
    id: number;
    level: number | null; // index in ITEMS, null if empty
}

export const PetAlchemy: React.FC = () => {
    const [slots, setSlots] = useState<Slot[]>(Array(TOTAL_SLOTS).fill(null).map((_, i) => ({ id: i, level: null })));
    const [draggedSlotId, setDraggedSlotId] = useState<number | null>(null);
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(parseInt(localStorage.getItem('pa_highscore') || '0'));
    const [nextSpawn, setNextSpawn] = useState(0); // Progress to next spawn

    // Spawn 2 initial items
    useEffect(() => {
        spawnItem();
        spawnItem();
    }, []);

    const spawnItem = () => {
        setSlots(prev => {
            const emptyIndices = prev.map((s, i) => s.level === null ? i : -1).filter(i => i !== -1);
            if (emptyIndices.length === 0) return prev;

            const randIndex = emptyIndices[Math.floor(Math.random() * emptyIndices.length)];
            const newSlots = [...prev];
            newSlots[randIndex] = { ...newSlots[randIndex], level: 0 }; // Level 0 = ITEM[0]
            return newSlots;
        });
    };

    const handleDragStart = (id: number) => {
        if (slots[id].level !== null) {
            setDraggedSlotId(id);
        }
    };

    const handleDrop = (targetId: number) => {
        if (draggedSlotId === null || draggedSlotId === targetId) return;

        const sourceLevel = slots[draggedSlotId].level;
        const targetLevel = slots[targetId].level;

        if (sourceLevel === null) return;

        const newSlots = [...slots];

        if (targetLevel === null) {
            // Move
            newSlots[targetId].level = sourceLevel;
            newSlots[draggedSlotId].level = null;
        } else if (sourceLevel === targetLevel) {
            // Merge
            if (sourceLevel < ITEMS.length - 1) {
                newSlots[targetId].level = sourceLevel + 1;
                newSlots[draggedSlotId].level = null;
                const points = Math.pow(2, sourceLevel + 1);
                const newScore = score + points;
                setScore(newScore);
                if (newScore > highScore) {
                    setHighScore(newScore);
                    localStorage.setItem('pa_highscore', newScore.toString());
                }
            } else {
                // Max level reached (no change or bounce?)
                return;
            }
        } else {
            // Swap? Or just do nothing? Standard merge games usually swap if different.
            // Let's swap to be friendly.
            newSlots[targetId].level = sourceLevel;
            newSlots[draggedSlotId].level = targetLevel;
        }

        setSlots(newSlots);
        setDraggedSlotId(null);
    };

    // Auto-spawn mechanic
    useEffect(() => {
        const interval = setInterval(() => {
            setNextSpawn(prev => {
                if (prev >= 100) {
                    spawnItem();
                    return 0;
                }
                return prev + 5;
            });
        }, 200);
        return () => clearInterval(interval);
    }, [slots]); // Dep on slots to stop spawning if full? No, spawnItem handles full.

    // Touch D&D logic - HTML5 D&D sucks on mobile, implementing simplified logic
    // We need to track touch move over elements
    const handleTouchEnd = (e: React.TouchEvent) => {
        if (draggedSlotId === null) return;

        const touch = e.changedTouches[0];
        const targetElement = document.elementFromPoint(touch.clientX, touch.clientY);
        const slotDiv = targetElement?.closest('[data-slot-id]');

        if (slotDiv) {
            const targetId = parseInt(slotDiv.getAttribute('data-slot-id') || '-1');
            if (targetId !== -1) {
                handleDrop(targetId);
            }
        }
        setDraggedSlotId(null);
    };

    return (
        <div className="h-full flex flex-col items-center justify-center bg-slate-900 p-4 select-none touch-none"
            onMouseUp={() => setDraggedSlotId(null)}
            onTouchEnd={handleTouchEnd}
        >
            <div className="flex w-full max-w-sm justify-between items-end mb-4">
                <div>
                    <h1 className="text-3xl font-black text-amber-500 uppercase italic tracking-tighter shadow-amber-500/20 drop-shadow-lg">Alquimia</h1>
                    <div className="text-xs font-mono text-slate-400">Arrastra para fusionar</div>
                </div>
                <div className="text-right">
                    <div className="bg-slate-800 px-4 py-2 rounded-xl border border-slate-700">
                        <div className="text-[10px] font-bold text-slate-500 uppercase">Score</div>
                        <div className="text-xl font-black text-white">{score}</div>
                    </div>
                </div>
            </div>

            {/* Generator Bar */}
            <div className="w-full max-w-sm mb-4 flex items-center gap-4">
                <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 transition-all duration-200" style={{ width: `${nextSpawn}%` }} />
                </div>
                <button onClick={spawnItem} className="bg-amber-600 hover:bg-amber-500 text-white p-2 rounded-lg text-xs font-bold uppercase active:scale-95 transition-transform">
                    + Items
                </button>
            </div>

            <div className="bg-slate-800 p-2 rounded-2xl shadow-2xl border border-slate-700/50 relative">
                <div className="grid grid-cols-5 gap-2 w-full max-w-sm aspect-square">
                    {slots.map((slot) => (
                        <div
                            key={slot.id}
                            data-slot-id={slot.id}
                            onMouseDown={() => handleDragStart(slot.id)}
                            onMouseUp={() => draggedSlotId !== null && handleDrop(slot.id)} // Desktop Drop
                            onTouchStart={() => handleDragStart(slot.id)} // Mobile Drag Start
                            className={`
                                w-full h-full rounded-lg flex flex-col items-center justify-center text-2xl relative
                                ${draggedSlotId === slot.id ? 'opacity-50 scale-110 z-50 bg-amber-500/20' : 'bg-slate-900/50'}
                                border border-white/5 transition-all duration-100
                            `}
                        >
                            {slot.level !== null && (
                                <>
                                    <span className="pointer-events-none select-none animate-in zoom-in duration-300 transform transition-transform">
                                        {ITEMS[slot.level]}
                                    </span>
                                    <span className="absolute bottom-0 right-1 text-[6px] font-mono text-slate-500 pointer-events-none">
                                        Lvl {slot.level + 1}
                                    </span>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex gap-4 mt-6">
                <button onClick={() => { setSlots(Array(TOTAL_SLOTS).fill(null).map((_, i) => ({ id: i, level: null }))); setScore(0); spawnItem(); spawnItem(); }} className="p-4 bg-slate-800 rounded-full hover:bg-slate-700 hover:text-white transition-colors border border-white/5">
                    <RotateCcw size={20} />
                </button>
            </div>

            <div className="mt-4 text-[10px] text-slate-600 font-mono text-center max-w-xs">
                Arrastra items iguales para crear nuevos objetos. ¡Llega al nivel máximo!
            </div>
        </div>
    );
};
